/* eslint-disable max-lines */
import { Col, Row } from 'antd';
import { t } from 'i18next';
import { observer } from 'mobx-react-lite';
import { Card } from 'src/components/Card/Card';
import { DataView } from 'src/components/DataView/DataView';
import {
    CreateFinalOfferRequestInstallmentOptionEnum,
    LoanApplicationDto,
} from 'src/generated-api-client';
import { ApplicationStatus } from 'src/stores/LoanApplicationsStore/LoanApplicationsStore.types';

type LoanApplicationFinalOfferProps = {
    application?: LoanApplicationDto;
};

export const LoanApplicationFinalOffer = observer(
    ({ application }: LoanApplicationFinalOfferProps) => {
        const isVisible =
            application?.status === ApplicationStatus.ACTIVE ||
            application?.status === ApplicationStatus.FULLY_PAID;

        const finalOffer = application?.finalOffer;

        if (!isVisible) {
            return null;
        }

        return (
            <Card title={t('LoanApplication.FinalOffer.Title')}>
                <Row gutter={[16, 16]}>
                    <Col xs={24} sm={12} md={12}>
                        <DataView
                            label={t('LoanApplication.FinalOffer.Name')}
                            value={finalOffer?.name}
                        />
                    </Col>
                    <Col xs={24} sm={12} md={12}>
                        <DataView
                            label={t(
                                'LoanApplication.FinalOffer.AgreementNumber',
                            )}
                            value={finalOffer?.agreementNumber}
                        />
                    </Col>

                    <Col xs={24} sm={12} md={12}>
                        <DataView
                            label={t(
                                'LoanApplication.FinalOffer.FinancingAmount',
                            )}
                            value={finalOffer?.financingAmount}
                            formatValue="currency-sar"
                        />
                    </Col>
                    <Col xs={24} sm={12} md={12}>
                        <DataView
                            label={t(
                                'LoanApplication.FinalOffer.FirstPaymentDate',
                            )}
                            value={finalOffer?.firstPaymentDate}
                            formatValue="date"
                        />
                    </Col>

                    <Col xs={24} sm={12} md={12}>
                        <DataView
                            label={t(
                                'LoanApplication.FinalOffer.FinalSettlementDate',
                            )}
                            value={finalOffer?.finalSettlementDate}
                            formatValue="date"
                        />
                    </Col>
                    <Col xs={24} sm={12} md={12}>
                        <DataView
                            label={t(
                                'LoanApplication.FinalOffer.PaymentFrequency',
                            )}
                            value={finalOffer?.paymentFrequency}
                        />
                    </Col>

                    <Col xs={24} sm={12} md={12}>
                        <DataView
                            label={t(
                                'LoanApplication.FinalOffer.InstallmentOption',
                            )}
                            value={finalOffer?.installmentOption}
                        />
                    </Col>

                    {finalOffer?.installmentOption ===
                        CreateFinalOfferRequestInstallmentOptionEnum.FixedValue && (
                        <Col xs={24} sm={12} md={12}>
                            <DataView
                                label={t(
                                    'LoanApplication.FinalOffer.InstallmentAmount',
                                )}
                                value={finalOffer?.installmentAmount}
                                formatValue="currency-sar"
                            />
                        </Col>
                    )}

                    {finalOffer?.installmentOption ===
                        CreateFinalOfferRequestInstallmentOptionEnum.Percentage && (
                        <Col xs={24} sm={12} md={12}>
                            <DataView
                                label={t(
                                    'LoanApplication.FinalOffer.DeductionRatePct',
                                )}
                                value={finalOffer?.deductionRatePct}
                                formatValue="percent"
                            />
                        </Col>
                    )}

                    {finalOffer?.installmentOption ===
                        CreateFinalOfferRequestInstallmentOptionEnum.Percentage && (
                        <>
                            <Col xs={24} sm={12} md={12}>
                                <DataView
                                    label={t(
                                        'LoanApplication.FinalOffer.HoldbackRate',
                                    )}
                                    value={finalOffer?.holdbackRatePct}
                                    formatValue="percent"
                                />
                            </Col>
                            <Col xs={24} sm={12} md={12}>
                                <DataView
                                    label={t(
                                        'LoanApplication.FinalOffer.MinimumInstallmentAmount',
                                    )}
                                    value={finalOffer?.minInstallmentAmount}
                                    formatValue="currency-sar"
                                />
                            </Col>
                            <Col xs={24} sm={12} md={12}>
                                <DataView
                                    label={t(
                                        'LoanApplication.FinalOffer.MaximumInstallmentAmount',
                                    )}
                                    value={finalOffer?.maxInstallmentAmount}
                                    formatValue="currency-sar"
                                />
                            </Col>
                        </>
                    )}

                    <Col xs={24} sm={12} md={12}>
                        <DataView
                            label={t(
                                'LoanApplication.FinalOffer.FinancingTerm',
                            )}
                            value={finalOffer?.term}
                        />
                    </Col>
                    <Col xs={24} sm={12} md={12}>
                        <DataView
                            label={t('LoanApplication.FinalOffer.MurabahaRate')}
                            value={finalOffer?.murabahaRatePct}
                            formatValue="percent"
                        />
                    </Col>
                    <Col xs={24} sm={12} md={12}>
                        <DataView
                            label={t(
                                'LoanApplication.FinalOffer.TotalMurabahaAmount',
                            )}
                            value={finalOffer?.totalMurabahaAmount}
                            formatValue="currency-sar"
                        />
                    </Col>
                    <Col xs={24} sm={12} md={12}>
                        <DataView
                            label={t(
                                'LoanApplication.FinalOffer.TotalRepayableAmount',
                            )}
                            value={finalOffer?.totalRepayableAmount}
                            formatValue="currency-sar"
                        />
                    </Col>
                    <Col xs={24} sm={12} md={12}>
                        <DataView
                            label={t(
                                'LoanApplication.FinalOffer.DisbursementAmount',
                            )}
                            value={finalOffer?.disbursementAmount}
                            formatValue="currency-sar"
                        />
                    </Col>
                    <Col xs={24} sm={12} md={12}>
                        <DataView
                            label={t('LoanApplication.FinalOffer.AdminFeeRate')}
                            value={finalOffer?.adminFeeRatePct}
                            formatValue="percent"
                        />
                    </Col>
                    <Col xs={24} sm={12} md={12}>
                        <DataView
                            label={t(
                                'LoanApplication.FinalOffer.AdminFeeAmount',
                            )}
                            value={finalOffer?.adminFeeAmount}
                            formatValue="currency-sar"
                        />
                    </Col>
                    <Col xs={24} sm={12} md={12}>
                        <DataView
                            label={t(
                                'LoanApplication.FinalOffer.AdminFeeAmountWithVat',
                            )}
                            value={finalOffer?.adminFeeWithVatAmount}
                            formatValue="currency-sar"
                        />
                    </Col>
                    <Col xs={24} sm={12} md={12}>
                        <DataView
                            label={t('LoanApplication.FinalOffer.Iban')}
                            value={finalOffer?.iban}
                        />
                    </Col>
                    <Col xs={24} sm={12} md={12}>
                        <DataView
                            label={t(
                                'LoanApplication.FinalOffer.CollectionCommissionPct',
                            )}
                            value={finalOffer?.collectionCommissionPct}
                            formatValue="percent"
                        />
                    </Col>
                </Row>
            </Card>
        );
    },
);
